import { useEffect, useState, useCallback } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Page } from '../../layout/AppLayout.jsx'
import Badge from '../../components/ui/Badge.jsx'
import { IconButton } from '../../components/ui/Button.jsx'
import Pagination from '../../components/ui/Pagination.jsx'
import { useCompany } from '../../context/CompanyContext.jsx'
import api from '../../lib/api.js'
import { formatDate } from '../../lib/format.js'
import { AdminTable, Td } from './AdminTable.jsx'
import { STEP_TONE } from './onboardingShared.js'

/* Admin — onboarding validation queue (inventory §4.1). Super-admin only.
 * `embedded`: rendered inside the admin console "Validation" tab (no Page chrome). */
export default function OnboardingList({ embedded = false }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isSuperAdmin } = useCompany()
  const [rows, setRows] = useState([])
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 })
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState('pending_validation')
  const [loading, setLoading] = useState(true)

  const load = useCallback(() => {
    setLoading(true)
    api.get('/admin/onboarding/pending', { params: { status, page, per_page: 20, sort: 'registration_started_at_desc' } })
      .then(({ data }) => {
        setRows(data?.onboardings || [])
        const total = data?.total ?? (data?.onboardings || []).length
        setPagination({ page: data?.page || 1, pages: Math.max(1, Math.ceil(total / (data?.per_page || 20))), total })
      }).finally(() => setLoading(false))
  }, [status, page])
  useEffect(() => { load() }, [load])

  if (!isSuperAdmin()) return <Navigate to="/app/dashboard" replace />

  const columns = [
    { key: 'company', label: t('onboarding.list.company') }, { key: 'vat', label: t('admin.companies.vat') },
    { key: 'rep', label: t('onboarding.list.representative') }, { key: 'email', label: t('admin.users.email') },
    { key: 'status', label: t('admin.companies.status') }, { key: 'date', label: t('onboarding.list.requested') },
    { key: 'actions', label: t('common.actions'), align: 'right' },
  ]

  const STATUSES = [
    { v: 'pending_validation', label: t('onboarding.status.pending_validation') },
    { v: 'validation_rejected', label: t('onboarding.status.validation_rejected') },
    { v: 'all', label: t('common.all') },
  ]
  const statusToggle = (
    <div style={{ display: 'inline-flex', background: 'var(--color-bg-lilac)', borderRadius: 10, padding: 3, gap: 2 }}>
      {STATUSES.map((s) => (
        <button key={s.v} onClick={() => { setStatus(s.v); setPage(1) }} style={{
          padding: '8px 16px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
          background: status === s.v ? '#fff' : 'transparent',
          color: status === s.v ? 'var(--color-primary)' : 'var(--color-text-muted)',
          boxShadow: status === s.v ? 'var(--shadow-card)' : 'none',
          transition: 'background .12s ease, color .12s ease',
        }}>{s.label}</button>
      ))}
    </div>
  )

  const body = (
    <>
      <AdminTable columns={columns} rows={rows} loading={loading} empty={t('onboarding.list.empty')}
        renderRow={(o) => (
          <>
            <Td strong>{o.company_name}{o.legal_representative_role && <div style={{ fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 400 }}>{o.legal_representative_role}</div>}</Td>
            <Td>{o.vat_number || '—'}</Td>
            <Td>{o.user_name || '—'}</Td>
            <Td>{o.user_email}</Td>
            <Td><Badge tone={STEP_TONE[o.current_step] || 'neutral'}>{t(`onboarding.status.${o.current_step}`, o.current_step)}</Badge></Td>
            <Td>{formatDate(o.registration_started_at)}</Td>
            <Td align="right"><IconButton icon="eye" title={t('onboarding.list.review')} onClick={() => navigate(`/app/admin/onboarding/${o.id}`)} /></Td>
          </>
        )} />
      {pagination.pages > 1 && (
        <Pagination page={pagination.page} totalPages={pagination.pages} total={pagination.total} unit={t('onboarding.list.unit')}
          onPage={(p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }) }} />
      )}
    </>
  )

  // Embedded inside the admin console "Validation" tab.
  if (embedded) {
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>{statusToggle}</div>
        {body}
      </>
    )
  }

  return (
    <Page title={t('onboarding.list.title')} action={statusToggle}>
      {body}
    </Page>
  )
}
